import { cn } from '@/lib/utils';
import { getTeamIcon } from '@/utils/teamIcons';
import { Ban } from 'lucide-react';

/**
 * Icon names offered for challenge groups (sections). A curated subset of
 * the shared team-icon name set (`@/utils/teamIcons`) — the mobile app
 * resolves `group.icon` through the same map, so every name here renders
 * on both surfaces.
 */
export const GROUP_ICON_OPTIONS = [
  'target',
  'flag',
  'crown',
  'trophy',
  'shield',
  'compass',
  'star',
  'rocket',
  'zap',
  'key',
  'lock',
  'gem',
  'mountain',
  'globe',
  'anchor',
  'puzzle',
  'lightbulb',
] as const;

const NO_ICON_LABEL = 'No icon (default)';

export interface GroupIconPickerProps {
  /** Selected icon name, or null for the default (no explicit icon). */
  value: string | null;
  onChange: (icon: string | null) => void;
  disabled?: boolean;
}

/** Compact tappable grid of group/section icons with a "none" state. */
export function GroupIconPicker({ value, onChange, disabled = false }: GroupIconPickerProps) {
  return (
    <div className="grid grid-cols-6 gap-2">
      <button
        type="button"
        onClick={() => onChange(null)}
        disabled={disabled}
        title={NO_ICON_LABEL}
        aria-label={NO_ICON_LABEL}
        aria-pressed={value === null}
        className={cn(
          'flex items-center justify-center h-9 w-9 rounded-lg border transition-all',
          value === null
            ? 'border-primary bg-primary/10 ring-2 ring-primary ring-offset-1'
            : 'border-muted hover:border-primary/50 hover:bg-muted/50',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        <Ban className="h-4 w-4 text-muted-foreground" />
      </button>
      {GROUP_ICON_OPTIONS.map((name) => {
        const Icon = getTeamIcon(name);
        const isSelected = value === name;
        return (
          <button
            key={name}
            type="button"
            onClick={() => onChange(name)}
            disabled={disabled}
            title={name}
            aria-label={`${name} icon`}
            aria-pressed={isSelected}
            className={cn(
              'flex items-center justify-center h-9 w-9 rounded-lg border transition-all',
              isSelected
                ? 'border-primary bg-primary/10 ring-2 ring-primary ring-offset-1'
                : 'border-muted hover:border-primary/50 hover:bg-muted/50',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            <Icon className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
}
